import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, COMPANY } from "@/lib/data";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    const onKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        document.querySelector('[data-testid="nav-mobile-toggle"]')?.focus();
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <header className={`sticky top-0 z-50 border-b border-white/10 bg-ink-deep/95 backdrop-blur transition-shadow duration-300 ${scrolled ? "shadow-soft" : ""}`} data-testid="site-header">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 h-[76px]">
          <Link to="/" data-testid="nav-logo-link" aria-label="Ferry Freight home" className="shrink-0"><Logo light testid="header-logo" /></Link>
          <nav className="hidden xl:flex items-center gap-1" aria-label="Primary" data-testid="desktop-navigation">
            {NAV_LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) => `px-3 py-2.5 text-sm font-medium rounded-full transition-colors ${isActive ? "text-brand-active" : "text-slate-200 hover:text-white"}`}>
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden xl:flex items-center gap-5">
            <a href={`tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`} data-testid="nav-phone-link" className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white transition-colors">
              <Phone className="h-4 w-4 text-brand-light" /> {COMPANY.phone}
            </a>
            <Link to="/quote" data-testid="nav-quote-button" className="group btn-pill primary-action px-5 py-2.5 text-sm hover:-translate-y-0.5 hover:shadow-glow">
              Get a Quote <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <button type="button" className="xl:hidden flex items-center justify-center h-11 w-11 rounded-xl border border-white/15 text-white hover:bg-white/10 transition-colors" onClick={() => setOpen((value) => !value)}
            data-testid="nav-mobile-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} aria-controls="mobile-navigation">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav id="mobile-navigation" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }}
            className="xl:hidden overflow-hidden border-t border-white/10 bg-ink-deep" aria-label="Mobile" data-testid="mobile-navigation">
            <div className="px-4 py-4 space-y-1 max-h-[calc(100dvh-77px)] overflow-y-auto" data-lenis-prevent>
              {NAV_LINKS.map((link) => (
                <NavLink key={link.to} to={link.to} data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  className={({ isActive }) => `block px-4 py-3 rounded-xl font-display text-base font-semibold transition-colors ${isActive ? "text-brand-active bg-white/5" : "text-slate-200 hover:bg-white/10"}`}>
                  {link.label}
                </NavLink>
              ))}
              <a href={`tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`} data-testid="nav-mobile-phone-link" className="flex items-center gap-2 px-4 py-3 text-slate-300 font-semibold">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
              <Link to="/quote" data-testid="nav-mobile-quote-button" className="btn-pill primary-action w-full px-5 py-3.5">
                Get a Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}